import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { setDefaultOptions } from 'date-fns';

import { getDateLocale } from '@/utils/date';
import { ALL_NAMESPACES, INLINE_RESOURCES, readLocale } from '@/i18n/localeSource';
import { CLIENT_PAGE_PATH, initialNamespaces } from '@/i18n/namespaceGroups';
import { isSubdomainSite } from '@/utils/subdomain';
import { BRAND_INTERPOLATION } from '@/config/brand';
import { LANGUAGE_STORAGE_KEY, languageFromBrowser, languageFromCountry, migrateStoredLanguage } from '@/i18n/detection';

// Geo-based language detection from Cloudflare CF-IPCountry header (via nginx cookie)
const geoDetector = {
  name: 'geoDetector',
  lookup(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    return languageFromCountry(document.cookie.match(/geo_country=([A-Z]{2})/)?.[1]);
  },
};

// The browser's languages, limited to the automatic ones (a "de-DE" browser gets English, see ./detection.ts)
const browserDetector = {
  name: 'autoNavigator',
  lookup(): string | undefined {
    if (typeof navigator === 'undefined') return undefined;
    return languageFromBrowser(navigator.languages?.length ? navigator.languages : [navigator.language ?? '']);
  },
};

// Translations are loaded on demand, one language at a time (see ./localeSource.ts and
// the "i18n-<lng>" chunks in vite.config.ts), instead of all the languages being
// bundled into the entry chunk.
const lazyLocaleBackend = {
  type: 'backend' as const,
  init() {},
  read: readLocale,
};

// Before detection reads it: drop a stored language that is no longer offered (de, pl, pt-PT) so detection picks again
migrateStoredLanguage(typeof window !== 'undefined' ? (() => { try { return window.localStorage; } catch { return undefined; } })() : undefined);

// Create language detector instance and add custom geo detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(geoDetector);
languageDetector.addDetector(browserDetector);

i18n
  .use(lazyLocaleBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    // Only the namespaces the opened page needs are fetched up front (see namespaceGroups.ts).
    ns: INLINE_RESOURCES
      ? ALL_NAMESPACES
      : initialNamespaces(ALL_NAMESPACES, window.location.pathname, isSubdomainSite()),
    // undefined in production (locale chunks are fetched by the backend above); unit tests
    // pass every translation inline so i18n is ready synchronously, as it used to be.
    resources: INLINE_RESOURCES,
    defaultNS: 'common',
    fallbackLng: 'en',

    // Language detection order:
    // 1. localStorage - the user's choice (en, es or pt-BR; anything else was dropped above)
    // 2. geoDetector - country from Cloudflare CF-IPCountry header (en, es or pt-BR only)
    // 3. autoNavigator - browser language preference (en, es or pt-BR only)
    // then fallbackLng (en). The stock navigator/htmlTag detectors are not used (see ./detection.ts).
    detection: {
      order: ['localStorage', 'geoDetector', 'autoNavigator'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },

    interpolation: {
      escapeValue: false, // React already escapes
      // {{brand}}, {{domain}} and {{supportEmail}} work in every translation without the caller passing them.
      defaultVariables: { ...BRAND_INTERPOLATION },
    },

    // React-specific options
    react: {
      useSuspense: true,
    },

    // Show missing keys in development
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      // A namespace still loading answers every key as missing (components with useSuspense:false render their English
      // defaults meanwhile): that is not a missing translation, so it is not worth a warning.
      if (process.env.NODE_ENV === 'development' && [lng].flat().some((l) => i18n.hasResourceBundle(l, ns))) {
        console.warn(`Missing translation: [${lng}][${ns}][${key}]`);
      }
    },
  });

// Keep date-fns formatting in sync with the active UI language, so weekday /
// day / month names localize without passing a locale to every format() call.
const syncDateFnsLocale = (lng: string) => {
  setDefaultOptions({ locale: getDateLocale(lng) });
};
syncDateFnsLocale(i18n.language || 'en');
i18n.on('languageChanged', syncDateFnsLocale);

// Keep <html lang> on the active UI language on every route (screen readers, search engines).
// This is the only place that sets it: a page-level Helmet <html lang> is removed again when that
// page unmounts, which left /register, /pricing and the app on the index.html default.
// The client pages (/q/, /i/) render their own strings, English only for now, and set lang themselves (lib/meta.ts).
const syncHtmlLang = (lng: string) => {
  if (typeof document === 'undefined' || !lng || lng === 'cimode') return;
  if (CLIENT_PAGE_PATH.test(window.location.pathname)) return;
  document.documentElement.lang = lng;
};
syncHtmlLang(i18n.language || 'en');
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
